import { motion, useReducedMotion } from "framer-motion";
import { Play, MessageCircle } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { FaSpotify } from "react-icons/fa";
import Layout from "@/components/layout/Layout";
import outlanderArt from "@/assets/outlander.png.asset.json";
import speakToMeArt from "@/assets/speak-to-me.png.asset.json";
import marthasHarbourArt from "@/assets/marthas-harbour.png.asset.json";
import abigailsSongArt from "@/assets/abigails-song.png.asset.json";
import alwaysYoursArt from "@/assets/always-yours.png.asset.json";
import singingSailorArt from "@/assets/singing-sailor.png.asset.json";
import bloodOfMyBloodArt from "@/assets/blood-of-my-blood.png.asset.json";
import ancientLoveArt from "@/assets/ancient-love.png.asset.json";
import sweetDecayArt from "@/assets/sweet-decay.png.asset.json";
import loveOfMineArt from "@/assets/love-of-mine.png.asset.json";

const singles = [
  { title: "Outlander", art: outlanderArt.url },
  { title: "Speak To Me", art: speakToMeArt.url },
  { title: "Martha's Harbour", art: marthasHarbourArt.url },
  { title: "Abigail's Song", art: abigailsSongArt.url },
];

// releaseDate drives the automatic "Out now" / "Coming soon" button (UK time, midnight)
const upcomingSingles = [
  { title: "Singing Sailor", date: "4 September 2026", releaseDate: new Date("2026-09-04T00:00:00+01:00"), art: singingSailorArt.url },
  { title: "Blood of My Blood", date: "18 September 2026", releaseDate: new Date("2026-09-18T00:00:00+01:00"), art: bloodOfMyBloodArt.url },
  { title: "Ancient Love", date: "2 October 2026", releaseDate: new Date("2026-10-02T00:00:00+01:00"), art: ancientLoveArt.url },
  { title: "Sweet Decay", date: "16 October 2026", releaseDate: new Date("2026-10-16T00:00:00+01:00"), art: sweetDecayArt.url },
  { title: "Love of Mine", date: "30 October 2026", releaseDate: new Date("2026-10-30T00:00:00+00:00"), art: loveOfMineArt.url },
  { title: "Always Yours", date: "16 November 2026", releaseDate: new Date("2026-11-16T00:00:00+00:00"), art: alwaysYoursArt.url },
];

const SPOTIFY_URL = "https://open.spotify.com/artist/3iP5TKieYruTr7XClnpjX1";
import { WHATSAPP_URL } from "@/lib/whatsapp";
export const EMAIL = "hello@oliviaflemming.co.uk";

const HomeTwo = () => {
  const reduceMotion = useReducedMotion();
  // Content is visible by default; a quick 300ms fade on mount, no scroll observer
  const fadeUp = reduceMotion
    ? { initial: false as const }
    : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };


  return (
    <Layout>
      {/* Hero */}
      <section id="hero" className="relative min-h-screen bg-black">
        <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
          <div className="relative z-30 flex flex-col justify-center px-8 md:px-10 lg:px-20 xl:px-28 py-20 md:py-16 order-2 md:order-1">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-lg"
            >
              <p className="text-primary text-xs md:text-sm tracking-[0.35em] uppercase mb-6 font-inter font-light">
                Singer · Songwriter · Artist
              </p>
              <h1 className="text-5xl md:text-5xl lg:text-7xl font-playfair font-normal text-white leading-[1.1] mb-6 tracking-tight">
                OLIVIA<br />FLEMMING
              </h1>
              <p className="text-white/80 text-lg md:text-lg lg:text-xl font-light italic font-inter mb-12">
                A modern voice with classical roots
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollTo("music")}
                  className="flex items-center gap-2.5 bg-primary/90 text-black px-8 py-3.5 rounded-full font-medium text-sm hover:bg-primary transition-all"
                >
                  <Play className="w-4 h-4" />
                  Listen
                </button>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 border border-white/40 text-white px-8 py-3.5 rounded-full font-medium text-sm hover:border-primary hover:text-primary transition-all"
                >
                  <FaWhatsapp className="w-4 h-4" />
                  Message me on WhatsApp
                </a>
              </div>
            </motion.div>
          </div>

          <div className="relative h-[42vh] min-h-[320px] md:h-auto md:min-h-screen order-1 md:order-2 overflow-hidden film-grain bg-secondary">
            <img
              src="/images/hero-artist-1400.webp"
              srcSet="/images/hero-artist-800.webp 800w, /images/hero-artist-1400.webp 1400w"
              sizes="(min-width: 768px) 50vw, 100vw"
              alt="Olivia Flemming"
              className="w-full h-full object-cover object-[center_18%] md:object-center"
              style={{ filter: "sepia(0.12) brightness(0.9) contrast(1.02)" }}
              loading="eager"
              fetchPriority="high"
            />
            {/* Mobile vertical fade */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/10 md:hidden" />
            {/* Mobile horizontal fade */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:hidden" />
          </div>
        </div>
        {/* Desktop seam fade: overlays the section so the image blends into the dark text column */}
        <div
          className="hidden md:block pointer-events-none absolute inset-0 z-20"
          style={{
            background:
              "linear-gradient(to right, hsl(0 0% 0%) 0%, hsl(0 0% 0%) 30%, hsl(0 0% 0% / 0.98) 42%, hsl(0 0% 0% / 0.92) 52%, hsl(0 0% 0% / 0.75) 62%, hsl(0 0% 0% / 0.48) 72%, hsl(0 0% 0% / 0.22) 82%, hsl(0 0% 0% / 0) 95%)",
          }}
        />
      </section>

      {/* About */}
      <section id="about" className="py-20 md:py-32 bg-secondary">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-12 items-center">
            <div className="aspect-[3/4] bg-muted rounded-2xl overflow-hidden">
              <img
                src={speakToMeArt.url}
                alt="Olivia Flemming portrait"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-white">
                About Olivia
              </h2>
              <p className="text-white/70 leading-relaxed text-lg">
                I'm a 27 year old artist from Birmingham, shaped by years spent growing up in Spain. A place that continues to influence both my sound and identity.
              </p>
              <p className="text-white/70 leading-relaxed">
                Classically trained, I now explore a wide range of genres, reworking music into something that feels entirely my own. More recently, I've begun writing original songs, opening up a more personal and expressive side of my artistry.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hotels and events */}
      <section id="hotels" className="relative py-20 md:py-32 bg-cherry-deep overflow-hidden film-grain">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--cherry)/0.55),transparent_65%)] pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Live lounge singer</p>
            <h2 className="text-4xl md:text-6xl font-playfair font-semibold text-white mb-6">
              Hotels &amp; Events
            </h2>
            <p className="text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
              Perfect for hotel lounges, black tie events, private functions and themed evenings.
            </p>
            <p className="text-white/60 leading-relaxed max-w-2xl mx-auto mt-4">
              Elegant live performances with atmosphere and strong vocals. A memorable experience for your guests.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 mb-14">
            {[
              { t: "Repertoire", items: ["Timeless standards", "Cinematic and movie songs", "Classical crossover", "Contemporary songs", "Original music"] },
              { t: "Themed events", items: ["James Bond evenings", "Black tie events", "Special occasions", "Private celebrations"], main: true },
              { t: "Performance options", items: ["One 45 minute performance", "Two 30 minute performances"] },
            ].map((c) => (
              <motion.div
                {...fadeUp}
                key={c.t}
                className={`rounded-2xl p-7 border ${c.main ? "border-gold bg-cherry/30 md:-translate-y-3" : "border-gold/20 bg-black/30"}`}
              >
                <h3 className="text-xl font-playfair text-gold mb-4">{c.t}</h3>
                <ul className="space-y-2 text-white/75">
                  {c.items.map((x) => <li key={x}>{x}</li>)}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <a
              href={`${WHATSAPP_URL}?text=${encodeURIComponent("Hi Olivia, I'd like to check your availability. Venue: \nDates: \nType of event: ")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-medium text-cherry-deep hover:opacity-90 transition"
            >
              <FaWhatsapp className="h-5 w-5" /> Message on WhatsApp for availability
            </a>
          </div>
        </div>
      </section>

      {/* What I sing */}
      <section className="relative py-20 md:py-28 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--cherry)/0.25),transparent_70%)] pointer-events-none" />
        <motion.div {...fadeUp} className="relative max-w-3xl mx-auto px-4 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">What I sing</p>
          <h2 className="text-3xl md:text-5xl font-playfair font-semibold text-white mb-8">A style to suit the room</h2>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {["Classical", "Contemporary", "Cinematic songs", "Crossover classics", "Original songs"].map((x) => (
              <span key={x} className="rounded-full border border-gold/40 px-5 py-2 text-white/85 text-sm">{x}</span>
            ))}
          </div>
          <p className="text-white/65 leading-relaxed">
            Refined and relaxed, or striking and theatrical. Every set is tailored to the venue and occasion.
          </p>
        </motion.div>
      </section>

      {/* Residential homes and retirement villages */}
      <section id="residential" className="relative py-20 md:py-32 bg-black overflow-hidden film-grain">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
        <div className="relative max-w-4xl mx-auto px-4">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Live performances</p>
            <h2 className="text-3xl md:text-5xl font-playfair font-semibold text-white mb-6">
              Residential Homes &amp; Retirement Villages
            </h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto">
              Warm, engaging sets designed for residents to enjoy. Atmosphere, entertainment and something special to the day.
            </p>
          </motion.div>
          <motion.ul {...fadeUp} className="grid sm:grid-cols-2 gap-4 mb-12">
            {["Familiar classics", "Relaxed lounge style performances", "Classical and crossover songs", "Contemporary favourites", "Themed performances for seasonal events and special occasions"].map((x) => (
              <li key={x} className="rounded-xl border border-gold/25 bg-gold/5 px-5 py-4 text-white/80">{x}</li>
            ))}
          </motion.ul>
          <div className="text-center">
            <a
              href={`${WHATSAPP_URL}?text=${encodeURIComponent("Hi Olivia, I'd like to ask about availability and packages for our residents.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-gold px-8 py-4 font-medium text-gold hover:bg-gold hover:text-black transition"
            >
              <FaWhatsapp className="h-5 w-5" /> Ask about availability and packages
            </a>
          </div>
        </div>
      </section>

      {/* Music / Releases */}
      <section id="music" className="py-20 md:py-32 bg-cream">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-white text-center mb-16">
              Music
            </h2>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-primary/30 md:-translate-x-1/2" />

              {/* Always Yours */}
              <div className="relative mb-16 md:mb-24">
                <div className="absolute left-4 md:left-1/2 top-0 w-3 h-3 bg-primary rounded-full md:-translate-x-1/2" />
                <div className="pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-12 md:items-center">
                  <div className="md:text-right md:order-1">
                    <p className="text-primary text-base uppercase tracking-widest font-semibold mb-2">16th November 2026</p>
                    <h3 className="text-2xl md:text-3xl font-playfair font-semibold text-white mb-4">
                      Always Yours
                    </h3>
                    <p className="text-white/60 leading-relaxed mb-6">
                      Always Yours is about love in all its different forms: the love we find, the love we lose, the love we hold onto, and the love that stays with us even when everything else changes. It explores memories, heartbreak, longing, family, devotion and the people who leave a permanent mark on our lives. At its heart, this album is about the idea that some connections never completely disappear.
                    </p>
                    <a
                      href="https://open.spotify.com/artist/3iP5TKieYruTr7XClnpjX1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-primary text-black px-6 py-3 rounded-full font-medium text-sm hover:bg-primary/90 transition-all w-fit"
                    >
                      <FaSpotify className="w-4 h-4" />
                      Follow on Spotify
                    </a>
                  </div>
                  <div className="md:order-2 mt-8 md:mt-0">
                    <div className="aspect-square bg-muted rounded-2xl overflow-hidden group">
                      <img
                        src={alwaysYoursArt.url}
                        alt="Always Yours album art"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Always Return */}
              <div className="relative mb-16 md:mb-24">
                <div className="absolute left-4 md:left-1/2 top-0 w-3 h-3 bg-white/60 rounded-full md:-translate-x-1/2" />
                <div className="pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-12 md:items-center">
                  <div className="md:order-2">
                    <p className="text-primary text-base uppercase tracking-widest font-semibold mb-2">Out Now: 11th June 2026</p>
                    <h3 className="text-2xl md:text-3xl font-playfair font-semibold text-white mb-4">
                      Always Return
                    </h3>
                    <p className="text-white/60 leading-relaxed mb-6">
                      Always Return is an album about home, belonging and the places that stay with us wherever life takes us.
                    </p>
                    <p className="text-white/60 leading-relaxed mb-6">
                      For me, that place is Spain. It is where I feel most at home, where I feel inspired to write and create, and somewhere that holds so many special memories for me. This album brings together songs that give me that same feeling of warmth, comfort and familiarity, alongside my very first original song.
                    </p>
                    <p className="text-white/60 leading-relaxed mb-6">
                      At its heart, Always Return is about finding somewhere that feels like a part of you. No matter how far I travel or how much life changes, Spain will always be somewhere I return to.
                    </p>
                    <a
                      href="https://open.spotify.com/album/57iabyclor3as37i7vbqx4"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-primary text-black px-6 py-3 rounded-full font-medium text-sm hover:bg-primary/90 transition-all w-fit"
                    >
                      <FaSpotify className="w-4 h-4" />
                      Play album on Spotify
                    </a>
                  </div>
                  <div className="md:order-1 mt-8 md:mt-0">
                    <div className="aspect-square bg-muted rounded-2xl overflow-hidden group">
                      <img
                        src="/images/album-artwork-1000.webp"
                        srcSet="/images/album-artwork-600.webp 600w, /images/album-artwork-1000.webp 1000w"
                        sizes="(min-width: 768px) 480px, 90vw"
                        alt="Always Return album art"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mb-6">
              <p className="text-primary text-sm uppercase tracking-widest font-medium mb-2">Always Yours</p>
              <h3 className="text-2xl md:text-3xl font-playfair font-semibold text-white mb-3">
                Release Roadmap
              </h3>
              <p className="text-white/60 max-w-2xl mx-auto">
                A run of singles leading up to the album, each with its own story to tell.
              </p>
            </div>
            <div className="relative mb-16 pl-10 md:pl-0">
              {/* Roadmap line */}
              <div className="absolute left-3 md:left-1/2 top-2 bottom-2 w-px bg-primary/30 md:-translate-x-1/2" />
              {upcomingSingles.map(({ title, date, art, releaseDate }, i) => {
                const isOut = new Date() >= releaseDate;
                return (
                <div key={title} className="relative mb-10 last:mb-0">
                  <div className="absolute -left-7 md:left-1/2 top-6 w-3 h-3 rounded-full bg-primary md:-translate-x-1/2 ring-4 ring-cream" />
                  <div className={`md:grid md:grid-cols-2 md:gap-12 md:items-center`}>
                    <div className={`${i % 2 === 0 ? "md:order-1 md:text-right" : "md:order-2"}`}>
                      <p className="text-primary text-base uppercase tracking-widest font-semibold mb-2">{date}</p>
                      <h4 className="text-xl md:text-2xl font-playfair text-white mb-3">{title}</h4>
                      {isOut ? (
                        <a
                          href={SPOTIFY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-primary text-black px-5 py-2 rounded-full text-xs uppercase tracking-widest hover:bg-primary/90 transition-all"
                        >
                          <FaSpotify className="w-3.5 h-3.5" />
                          Out now
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-2 border border-primary/40 text-primary px-5 py-2 rounded-full text-xs uppercase tracking-widest">
                          Coming soon
                        </span>
                      )}
                    </div>
                    <div className={`mt-4 md:mt-0 ${i % 2 === 0 ? "md:order-2" : "md:order-1"}`}>
                      <a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" className="group block">
                        <div className="aspect-square rounded-xl overflow-hidden bg-muted max-w-[280px] md:max-w-none">
                          <img
                            src={art}
                            alt={`${title}. Olivia Flemming single artwork`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>


            <div className="text-center mb-6">
              <p className="text-primary text-sm uppercase tracking-widest font-medium mb-2">Listen on Spotify</p>
              <h3 className="text-2xl md:text-3xl font-playfair font-semibold text-white">
                Top Tracks
              </h3>
            </div>
            <div className="bg-muted rounded-2xl p-2 overflow-hidden">
              <iframe
                title="Olivia Flemming on Spotify"
                src="https://open.spotify.com/embed/artist/3iP5TKieYruTr7XClnpjX1?utm_source=generator&theme=0"
                width="100%"
                height="600"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-xl"
              />
            </div>
            <div className="text-center mt-6">
              <a
                href="https://open.spotify.com/artist/3iP5TKieYruTr7XClnpjX1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-black px-6 py-3 rounded-full font-medium text-sm hover:bg-primary/90 transition-all"
              >
                <FaSpotify className="w-4 h-4" />
                Follow on Spotify
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Videos */}
      <section id="videos" className="py-20 md:py-32 bg-secondary">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div {...fadeUp}>
            <p className="text-primary text-sm uppercase tracking-widest font-medium text-center mb-3">Music Videos</p>
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-white text-center mb-12">
              Videos
            </h2>
            <div className="bg-muted rounded-2xl p-2 overflow-hidden">
              <div className="aspect-video rounded-xl overflow-hidden">
                <iframe
                  title="Always Return music video"
                  src="https://www.youtube.com/embed/imUlSEc8CBI"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  className="w-full h-full"
                />
              </div>
            </div>
            <p className="text-center text-white/60 mt-4 font-playfair text-lg">
              Always Return
            </p>
          </motion.div>
        </div>
      </section>


      {/* Visual Gallery — Single Artworks */}
      <section id="gallery" className="py-20 md:py-32 bg-secondary">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div {...fadeUp}>
            <p className="text-primary text-sm uppercase tracking-widest font-medium text-center mb-3">Singles on Spotify</p>
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-white text-center mb-16">
              Visuals
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
              {[...singles, ...upcomingSingles].filter(({ title }) => title !== "Always Yours").map(({ title, art }) => (
                <a
                  key={title}
                  href={SPOTIFY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-square rounded-xl overflow-hidden bg-muted"
                >
                  <img
                    src={art}
                    alt={`${title}. Olivia Flemming single artwork`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <p className="text-white font-playfair text-lg leading-tight">{title}</p>
                    <p className="flex items-center gap-1.5 text-primary text-xs mt-1">
                      <FaSpotify className="w-3 h-3" /> Listen on Spotify
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>




      {/* Contact */}
      <section id="contact" className="py-20 md:py-32 bg-secondary">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-white mb-6">
              Let's Create Something Together
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">
              Open to collaborations, features, and creative projects. Let's talk.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-primary text-black px-8 py-4 rounded-full font-semibold text-base hover:bg-primary/90 transition-all"
            >
              <FaWhatsapp className="w-5 h-5" />
              Message me on WhatsApp
            </a>
            <p className="text-white/60 mt-6">
              Or email{" "}
              <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">
                {EMAIL}
              </a>
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default HomeTwo;
